import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import WorkIcon from "@mui/icons-material/Work";
import Box from "@mui/material/Box";
import { styled, Theme } from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import CompanyColumnCell from "_shared/teams/cells/company-column-cell";
import LeaderColumnCell from "_shared/teams/cells/leader-column-cell";
import MembersColumnCell from "_shared/teams/cells/members-column-cell";
import { PageTab } from "_shared/teams/teams-tabs";
import _ from "lodash";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_Row as Row,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import { startDraftProposalFromTeamTemplate } from "work/proposals/_views/drafts/start-draft-proposal-from-template";
import {
  useArchiveTeamTemplateMutation,
  useDeleteTeamTemplateMutation,
  useGetTeamTemplatesQuery,
  useUnarchiveTeamTemplateMutation,
} from "work/team-templates/_api/team-template-api";
import { TeamTemplateOrderField } from "work/team-templates/_api/queries/team-template-order-field";
import TeamTemplateQuery, {
  TeamTemplateFilterParameters,
  TeamTemplateOrderParameters,
} from "work/team-templates/_api/queries/team-template-query";
import {
  getErrorLoadingTeamTemplatesByQuery,
  getIsLoadingTeamTemplatesByQuery,
  getTeamTemplatesByQuery,
  getTeamTemplatesQueryByInterface,
  updateTeamTemplatesInterfaceQuery,
} from "work/team-templates/_store/team-templates-redux-slice";
import TeamTemplate from "work/team-templates/team-template";
import {
  LowerTopToolbarContainer,
  RowTitle,
  TableTitle,
  TableTopToolbarContainer,
  TeamsTabs,

  ToolbarButtonsContainer,
} from "./teams-page";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import useSessionUser from "permissions/session/use-session-user";
import Loader from "_shared/_views/loading/loader";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import RowActions, { type RowAction } from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import {
  MobileToolbarButtonsContainer,
  TableTitleRow,
  searchFieldSpacing,
  ToolbarButtons,
  searchTextFieldProps,
  topToolbarFitsThePage,
} from "_shared/_views/tables/table-toolbar";

const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

/**
 * Where a row goes, in one place, because the row and the link in it have to agree — and because
 * the archived tab sends the same row somewhere else.
 */
function teamTemplateDestination(template: TeamTemplate, activeTab: PageTab): string | undefined {
  if (!template.id) return undefined;
  return activeTab === PageTab.Archived
    ? `/teams/archived/view/${template.id.value}`
    : `/teams/templates/edit/${template.id.value}`;
}

const columnHelper = createMRTColumnHelper<TeamTemplate>();

export default function Teams() {
  // State
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [globalFilter, setGlobalFilter] = React.useState<string | undefined>();

  // Route
  const routeParams = useLoaderData();
  const activeTab: PageTab = routeParams.pageTab ? (routeParams.pageTab as PageTab) : PageTab.Templates;
  // Named for what they are rather than what the tab is: the search box and the message that stands
  // in for the rows share this, and "No templates" on a page reached from Teams could be any of the
  // three kinds of template in the app.
  const searchSubject = activeTab === PageTab.Archived ? "archived team templates" : "team templates";

  // Hooks
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();

  // Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionUser = useSessionUser();
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  const interfaceName = "TeamsPage";
  const teamTemplatesQuery = useSelector(getTeamTemplatesQueryByInterface(interfaceName));
  const teamTemplatesApiQuery = useGetTeamTemplatesQuery(
    teamTemplatesQuery ? { query: teamTemplatesQuery, interfaceName } : skipToken,
  );
  const teamTemplatesResponse = useSelector(getTeamTemplatesByQuery(teamTemplatesQuery));
  const isLoadingTeamTemplates =
    useSelector(getIsLoadingTeamTemplatesByQuery(teamTemplatesQuery)) ||
    teamTemplatesApiQuery.isLoading ||
    teamTemplatesApiQuery.isFetching;
  const errorLoadingTeamTemplates = useSelector(getErrorLoadingTeamTemplatesByQuery(teamTemplatesQuery)) || teamTemplatesApiQuery.isError;
  // What the failure was, in the response's own words. Read from the query rather than from the
  // slice's copy: the slice substitutes "An error occurred." where it can make nothing of a
  // rejection, and passing that on would replace the failure state's line with a shorter one that
  // says less. This is `undefined` only when the query carries no rejection to read — which the
  // store can still outlive, so the generic fallbacks below stay.
  const errorLoadingTeamTemplatesMessage = getErrorMessageFromUnknown(teamTemplatesApiQuery.error);
  const [deleteTeamTemplate] = useDeleteTeamTemplateMutation();
  const [archiveTeamTemplate] = useArchiveTeamTemplateMutation();
  const [unarchiveTeamTemplate] = useUnarchiveTeamTemplateMutation();

  useEffect(() => {
    if (sessionPermissions || sessionPermissionsError || sessionPermissionsLoading) return;
    dispatch(populateSessionPermissions({ session }));
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  const teamTemplateUserIds = React.useMemo(
    () =>
      teamTemplatesResponse?.data.reduce<Guid[]>((ids: Guid[], template: TeamTemplate) => {
        ids.push(template.leader.userId);
        template.members.forEach((member) => ids.push(member.userId));
        return _.uniqBy(ids, (id) => id.value);
      }, []) ?? [],

    [teamTemplatesResponse],
  );

  const defaultTeamTemplatesQuery = React.useMemo(() => {
    return new TeamTemplateQuery({
      filterParameters: TeamTemplateFilterParameters.create({
        creatorEntityId: session.user?.legalEntityId,
        name: columnFilters.find((filter) => filter.id === "name")?.value as string,
        isArchived: routeParams.pageTab === PageTab.Archived,
      }),
      orderParameters: TeamTemplateOrderParameters.create({
        orderBy: TeamTemplateOrderField.Name,
        orderDir: OrderDirection.Ascending,
      }),
      paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: 10 }),
    });
  }, [columnFilters, routeParams.pageTab, session.user?.legalEntityId]);

  useEffect(() => {
    if (teamTemplatesQuery) return;

    dispatch(
      updateTeamTemplatesInterfaceQuery({
        interfaceName,
        query: defaultTeamTemplatesQuery,
      }),
    );
  }, [defaultTeamTemplatesQuery, dispatch, teamTemplatesQuery]);

  useGetIndividualsByUserIdsQuery(
    { userIds: new Set(teamTemplateUserIds.map((id) => id.value)) },
    { skip: teamTemplateUserIds.length === 0 },
  );

  const nameFilter = React.useMemo(() => columnFilters.find((filter) => filter.id === "name")?.value as string, [columnFilters]);
  const leaderFilter = React.useMemo(() => columnFilters.find((filter) => filter.id === "leader")?.value as string, [columnFilters]);
  const memberFilter = React.useMemo(() => columnFilters.find((filter) => filter.id === "members")?.value as string, [columnFilters]);

  const query = React.useMemo(() => {
    return new TeamTemplateQuery({
      filterParameters: TeamTemplateFilterParameters.create({
        creatorEntityId: session.user?.legalEntityId,
        name: nameFilter,
        leaderEntityId: leaderFilter ? new Guid(leaderFilter) : undefined,
        teamMemberEntityId: memberFilter ? new Guid(memberFilter) : undefined,
        isArchived: routeParams.pageTab === PageTab.Archived,
      }),
      orderParameters: TeamTemplateOrderParameters.create({
        orderBy: TeamTemplateOrderField.Name,
        orderDir: sorting[0]?.desc === false ? OrderDirection.Ascending : OrderDirection.Descending,
      }),
      paginationParameters: PaginationParameters.create(pagination),
    });
  }, [leaderFilter, memberFilter, nameFilter, pagination, routeParams.pageTab, session.user?.legalEntityId, sorting]);

  // Handle URL changes
  useEffect(() => {
    // Listing templates asks for teams access at need-to-know read-only, which is what every member
    // of an organization holds. Signing authority was standing in for that grant and answering a
    // different question, so members who may read the roster saw an empty page (issue #4651).
    if (!sessionUser?.legalEntityId || !sessionUser.canAccessTeams()) return;

    if (!routeParams.pageTab) {
      navigate(`/teams/${PageTab.Templates}`);
    }

    if (query.cacheKey === teamTemplatesQuery?.cacheKey) return;

    dispatch(updateTeamTemplatesInterfaceQuery({ interfaceName, query }));
  // `sessionUser` is the same instance across renders — its grants are attached to it, so the
  // answer above changes without its identity changing. `sessionPermissions` is what actually moves
  // when the grants land, so it is the dependency that re-runs this.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch, 
    navigate, 
    query?.cacheKey, 
    routeParams.pageTab, 
    sessionPermissions,
    sessionUser,
    session.user?.legalEntityId?.value, 
    teamTemplatesQuery?.cacheKey
  ]);

  async function handleDeleteTeamTemplate(id: Guid) {
    try {
      const response = await confirm({
        title: "Delete?",
        message: `Delete team template?`,
        okButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      await deleteTeamTemplate({ templateId: id.value }).unwrap();

      enqueueSnackbar("Deleted vendor-team template", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete vendor-team template. Please try again", {
        variant: "error",
      });
    }
  }

  async function handleArchiveTeamTemplate(id: Guid, archive: boolean) {
    const action = archive ? "Archive" : "Unarchive";

    try {
      const response = await confirm({
        title: `${action}?`,
        message: `${action} team template?`,
        okButtonText: action,
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      if (archive) {
        await archiveTeamTemplate({ templateId: id.value }).unwrap();
      } else {
        await unarchiveTeamTemplate({ templateId: id.value }).unwrap();
      }

      enqueueSnackbar(`${action}d vendor-team template`, { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Failed to ${action.toLowerCase()} vendor-team template. Please try again`, {
        variant: "error",
      });
    }
  }

  const handleCreateProposalFromTeamTemplate = React.useCallback((template: TeamTemplate) => {
    startDraftProposalFromTeamTemplate({
      dispatch,
      navigate,
      session,
      template,
    });
  }, [dispatch, navigate, session]);

  const renderLeaderColumn = React.useCallback((team: TeamTemplate) => {
    return <LeaderColumnCell teamTemplate={team} teamUserIds={teamTemplateUserIds} />;
  }, [teamTemplateUserIds]);

  const renderMembersColumn = React.useCallback((template: TeamTemplate) => {
    return <MembersColumnCell teamTemplate={template} teamUserIds={teamTemplateUserIds} />;
  }, [teamTemplateUserIds]);

  const renderCompanyColumn = React.useCallback((template: TeamTemplate) => {
    return <CompanyColumnCell teamTemplate={template} teamUserIds={teamTemplateUserIds} />;
  }, [teamTemplateUserIds]);

  const columns = React.useMemo(() => [
    columnHelper.accessor((team) => team.name?.value ?? "", {
      header: "Name",
      id: "name",
      Cell: ({ row }: { row: Row<TeamTemplate> }) => (
        <RowTitle>
          <RowLink to={teamTemplateDestination(row.original, activeTab)}>{row.original.name?.value ?? ""}</RowLink>
        </RowTitle>
      ),
      enableColumnFilter: false,
    }),
    columnHelper.accessor(renderCompanyColumn, {
      header: "Company",
      id: "company",
      enableSorting: false,
      enableColumnFilter: false,
    }),
    columnHelper.accessor(renderLeaderColumn, {
      header: "Leader",
      id: "leader",
      enableSorting: false,
      enableColumnFilter: false,
    }),
    columnHelper.accessor(renderMembersColumn, {
      header: "Members",
      id: "members",
      enableSorting: false,
      enableColumnFilter: false,
    }),
  ], [activeTab, renderCompanyColumn, renderLeaderColumn, renderMembersColumn]);

  const tableData = React.useMemo(() => teamTemplatesResponse?.data ?? [], [teamTemplatesResponse]);

  // Creating a team template needs entity-wide access, a strictly higher bar than the read access
  // that gets someone onto this page. Saying so on the button beats letting them build a whole
  // template and meet the API's 403 at Save (issue #4948).
  const canCreateTeamTemplate = sessionUser?.canCreateTeamTemplates() ?? false;
  const createTeamTemplateTooltip = !sessionPermissions && !sessionPermissionsError
    ? "Loading team permissions..."
    : canCreateTeamTemplate
      ? "Create Template"
      : "Creating a team template requires entity-wide team access. Ask an administrator at your firm.";

  const tableOptions: MRT_TableOptions<TeamTemplate> = {
    columns,
    data: tableData,
    enableRowSelection: false,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        const destination = teamTemplateDestination(row.original, activeTab);
        if (destination) navigate(destination);
      },
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <Tooltip title={createTeamTemplateTooltip}>
              <span>
                <Button
                  startIcon={<GroupAddIcon />}
                  disabled={!canCreateTeamTemplate}
                  onClick={() => navigate("/teams/templates/create")}
                >
                  Create Template
                </Button>
              </span>
            </Tooltip>
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitleRow>
            <TableTitle variant="h4">Teams</TableTitle>
            {isMediumDisplaySize && (
              <Tooltip title={createTeamTemplateTooltip}>
                <span>
                  <NewButton
                    color="primary"
                    disabled={!canCreateTeamTemplate}
                    onClick={() => navigate("/teams/templates/create")}
                  >
                    <GroupAddIcon fontSize="large" />
                  </NewButton>
                </span>
              </Tooltip>
            )}
          </TableTitleRow>
          <LowerTopToolbarContainer>
            <TeamsTabs activeTab={activeTab} />
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <MobileToolbarButtonsContainer>
              <GlobalFilterTextField table={table} />
              <ToolbarButtons>
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </MobileToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    getRowId: (row) => row.id?.value ?? "",
    enableRowActions: true,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
      // The search box belongs in the toolbar rather than behind a magnifier that has to be found
      // first, which is how the other list pages in the app present it.
      showGlobalFilter: true,
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) => searchTextFieldProps({ searches: searchSubject, table }),
    renderEmptyRowsFallback: emptyRowsFallback({
      errorDescription: errorLoadingTeamTemplatesMessage,
      isError: Boolean(errorLoadingTeamTemplates),
      isLoading: isLoadingTeamTemplates,
      onRetry: () => void teamTemplatesApiQuery.refetch(),
      subject: searchSubject,
    }),
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    renderRowActions: ({ row }) => {
      if (activeTab !== PageTab.Templates && activeTab !== PageTab.Archived) return null;
      const templateName = row.original.name?.value ?? "team template";
      const isArchivedTab = activeTab === PageTab.Archived;
      const actions: RowAction[] = [];

      if (activeTab === PageTab.Templates) {
        actions.push(
          {
            key: "create-proposal",
            label: "Create Proposal",
            ariaLabel: `Create proposal from ${templateName}`,
            icon: <WorkIcon />,
            onClick: () => handleCreateProposalFromTeamTemplate(row.original),
          },
          {
            key: "delete",
            label: "Delete",
            ariaLabel: `Delete ${templateName}`,
            icon: <DeleteIcon />,
            destructive: true,
            onClick: () => {
              if (!row.original.id) return;
              void handleDeleteTeamTemplate(row.original.id);
            },
          },
        );
      }

      actions.push({
        key: "archive",
        label: isArchivedTab ? "Unarchive" : "Archive",
        ariaLabel: `${isArchivedTab ? "Unarchive" : "Archive"} ${templateName}`,
        icon: isArchivedTab ? <UnarchiveIcon /> : <ArchiveIcon />,
        onClick: () => {
          if (!row.original.id) return;
          void handleArchiveTeamTemplate(row.original.id, !isArchivedTab);
        },
      });

      return <RowActions actions={actions} ariaLabel={`Actions for ${templateName}`} />;
    },
    muiTableContainerProps: {
      sx: {
        flex: 1,
        minWidth: 0
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden"
      },
    },
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    // The API's own words where there are any. "Error loading data" was all this said about any
    // failure, which is all a reviewer refused the whole list was told (#5235); it stays as the
    // fallback because the empty area below already names the subject.
    muiToolbarAlertBannerProps: errorLoadingTeamTemplates
      ? {
        color: "error",
        children: errorLoadingTeamTemplatesMessage ?? "Error loading data",
      }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: teamTemplatesResponse?.totalElements ?? 0,
    state: {
      columnFilters,
      globalFilter,
      isLoading: !teamTemplatesResponse?.data.length && isLoadingTeamTemplates,
      pagination,
      showAlertBanner: Boolean(errorLoadingTeamTemplates),
      showProgressBars: isLoadingTeamTemplates,
      sorting,
      showSkeletons: !teamTemplatesResponse && isLoadingTeamTemplates,
    },
  }

  if (!sessionPermissions) return <LoaderContainer><Loader fontSize="20px" /></LoaderContainer>;
  if (!sessionPermissions.includesAccessToTeams) return <NotAuthorized />;

  return <MRTNoMemo<TeamTemplate> options={tableOptions} />;
}
